import React, { useState } from 'react';
import EditFinancialsModal from './EditFinancialsModal';

const ClientFinancials = ({ submissions, invoices, payments, totals, onRefresh }) => {
    const [editingSubmissionId, setEditingSubmissionId] = useState(null);
    const [editingFinancial, setEditingFinancial] = useState(null);

    // Extract financial info from wizard submissions
    const financialData = submissions
        .map(sub => ({ data: sub.financial_info, submissionId: sub.id }))
        .filter(item => item.data && Object.keys(item.data).length > 0);

    return (
        <>
            {editingSubmissionId && editingFinancial && (
                <EditFinancialsModal
                    submissionId={editingSubmissionId}
                    financialData={editingFinancial}
                    onClose={() => {
                        setEditingSubmissionId(null);
                        setEditingFinancial(null);
                    }}
                    onSave={onRefresh}
                />
            )}

            <div className="space-y-6">
                {/* Financial Summary from Wizard */}
                {financialData.length > 0 && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">💰 Financial Information (Wizard)</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            {financialData.map((item, index) => (
                                <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600 relative">
                                    {item.submissionId && (
                                        <button
                                            onClick={() => {
                                                setEditingSubmissionId(item.submissionId);
                                                setEditingFinancial(item.data);
                                            }}
                                            className="absolute top-4 right-4 text-xs px-3 py-1.5 rounded bg-blue-600 text-white border border-blue-500 hover:bg-blue-700"
                                        >
                                            ✏️ Edit
                                        </button>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {item.data.annualIncome && (
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Annual Income</div>
                                                <div className="text-white font-mono font-bold">{item.data.annualIncome}</div>
                                            </div>
                                        )}
                                        {item.data.savingsAmount && (
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Savings Amount</div>
                                                <div className="text-white font-mono">{item.data.savingsAmount}</div>
                                            </div>
                                        )}
                                        {item.data.fundingSource && (
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Funding Source</div>
                                                <div className="text-white font-mono">{item.data.fundingSource}</div>
                                            </div>
                                        )}
                                        {item.data.sponsorAvailable !== undefined && (
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Sponsor Available</div>
                                                <div className="text-white font-mono">{item.data.sponsorAvailable ? 'Yes' : 'No'}</div>
                                            </div>
                                        )}
                                        {item.data.currency && (
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Currency</div>
                                                <div className="text-white font-mono">{item.data.currency}</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Display all other fields as JSON */}
                                    {Object.keys(item.data).length > 5 && (
                                        <details className="mt-4">
                                            <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300">View All Fields</summary>
                                            <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-auto">
                                                {JSON.stringify(item.data, null, 2)}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            {/* Invoices & Payments Summary */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">💰 Financial Summary</h3>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="p-4 bg-gray-700/50 rounded border border-gray-600">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Invoiced</div>
                            <div className="text-2xl font-bold text-white">{totals.invoiced.toLocaleString()}</div>
                        </div>
                        <div className="p-4 bg-gray-700/50 rounded border border-gray-600">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Paid</div>
                            <div className="text-2xl font-bold text-green-400">{totals.paid.toLocaleString()}</div>
                        </div>
                        <div className="p-4 bg-gray-700/50 rounded border border-gray-600">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Outstanding</div>
                            <div className={`text-2xl font-bold ${totals.outstanding > 0 ? 'text-amber-400' : 'text-gray-400'}`}>
                                {totals.outstanding.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Recent Invoices */}
                        <div>
                            <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">📄 Recent Invoices</h4>
                            <div className="space-y-2">
                                {invoices.slice(0, 5).map(inv => (
                                    <div key={inv.id} className="p-3 bg-gray-700/50 rounded border border-gray-600 flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-mono text-white">{inv.id.slice(0, 8)}</div>
                                            <div className="text-xs text-gray-400">{inv.due_date || '-'}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-white">{Number(inv.amount_due || 0).toLocaleString()}</div>
                                            <span className={`text-xs px-2 py-0.5 rounded ${inv.status === 'paid' ? 'bg-green-900/30 text-green-300' : 'bg-yellow-900/30 text-yellow-300'}`}>
                                                {inv.status || 'sent'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {invoices.length === 0 && (
                                    <div className="text-sm text-gray-400 text-center py-8">No invoices</div>
                                )}
                            </div>
                        </div>

                        {/* Recent Payments */}
                        <div>
                            <h4 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">💳 Recent Payments</h4>
                            <div className="space-y-2">
                                {payments.slice(0, 5).map(pay => (
                                    <div key={pay.id} className="p-3 bg-gray-700/50 rounded border border-gray-600 flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-mono text-white">{pay.id.slice(0, 8)}</div>
                                            <div className="text-xs text-gray-400">{pay.method || '-'}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-green-400">{Number(pay.amount || 0).toLocaleString()}</div>
                                            <div className="text-xs text-gray-400">{pay.paid_at ? new Date(pay.paid_at).toLocaleDateString() : '-'}</div>
                                        </div>
                                    </div>
                                ))}
                                {payments.length === 0 && (
                                    <div className="text-sm text-gray-400 text-center py-8">No payments</div>
                                )}
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClientFinancials;
